import React, { useEffect, useMemo, useState } from 'react';
import { useNavigation } from '@react-navigation/native';
import { View, KeyboardAvoidingView, useWindowDimensions, FlatList, TouchableOpacity, StyleSheet, Keyboard, Pressable } from 'react-native';
import Popover from 'react-native-popover-view/dist/Popover';
import { appColors, appFonts, fontSize } from '../../themes/appTheme';
import CommonHeader from '../../shared/commonComponents/commonHeader';
import CommonDialogBox from '../../shared/commonComponents/commonDialogBox';
import InviteUser from './inviteUser';
import { Theme, ThemeConsumer } from "@rneui/themed";
import { config } from '../../shared/environment';
import { NavigationProps, setRoleProps } from './roleModule';
import { getScreenPermission, useAppDispatch, useAppSelector } from '../../redux/hooks';
import { deletePosRole, getAllRoles } from '../../redux/slices/rolesSlice';
import { ZenBadge, ZenEmptyComponent, ZenIcon, ZenLoader, ZenText } from 'zen-core-elements';
import Toast from 'react-native-toast-message';
import ZenToast, { ZenToastConfig } from 'zen-core-elements/src/elements/ZenToastMessage/zenToast';
import { CustomModal } from '../../shared/commonComponents/customModal';
import { ListToolbar } from '../../shared/commonComponents/listToolbar';
import { shallowEqual } from 'react-redux';
import { showApiError } from '../../shared/utils/handleApiError';

const LIMIT = 10;

const RoleList = () => {
    const dispatch = useAppDispatch();
    const navigation = useNavigation<NavigationProps>();
    const { width, height } = useWindowDimensions();
    const { isWrite: isRoleWrite } = useAppSelector(getScreenPermission("RoleStack"), shallowEqual)
    // ─── Redux state ───────────────────────────────────────────────────────────
    const { loading, value: rolesReduxData } = useAppSelector((state) => state.roles);
    const allRoleRows = rolesReduxData?.roles?.rows ?? [];

    // ─── Local state ───────────────────────────────────────────────────────────
    const [offset, setOffset] = useState(0);
    const [listRefresh, setListRefresh] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [openInviteUser, setOpenInviteUser] = useState(false);
    const [roleInvite, setRoleInvite] = useState<setRoleProps | null>(null);
    const [isRemovePopover, setIsRemovePopover] = useState(false);
    const [selectRoleId, setSelectRoleId] = useState<number | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    // ─── Initial fetch on mount ────────────────────────────────────────────────
    useEffect(() => {
        fetchRoles();
    }, []);

    // ─── API call — fetch ALL roles once (no offset/limit/search sent to API) ──
    const fetchRoles = (forceRefresh = false) => {
        if (!forceRefresh && rolesReduxData) return;
        dispatch(getAllRoles({ storeId: config?.storeId }))
            .unwrap()
            .catch(showApiError).finally(() => {
                setOffset(0);
                setListRefresh(false);
            });
    };

    // ─── Local search filter ──────────────────────────────────────────────────
    const filteredRoles = useMemo(() => {
        const query = searchText.trim().toLowerCase();
        if (!query) return allRoleRows;
        return allRoleRows.filter((role) =>
            role.name?.toLowerCase().includes(query) ||
            role.description?.toLowerCase().includes(query)
        );
    }, [allRoleRows, searchText]);

    // ─── Local pagination ─────────────────────────────────────────────────────
    const visibleRoles = useMemo(() => {
        const filteredData = filteredRoles.slice(0, offset + LIMIT)
        if (filteredData?.length % 2 == 1) {
            filteredData.push({
                type: "EMPTY",
                name: "EMPTY",
                code: "EMPTY",
                description: "EMPTY",
                createdat: "EMPTY",
                id: 0,
                activeUsers: 0
            })
        }
        return filteredData
    }, [filteredRoles, offset]);
    const localIsReached = visibleRoles.length >= filteredRoles.length;

    // Reset local offset when search changes
    useEffect(() => {
        setOffset(0);
    }, [searchText]);

    // useEffect(() => {
    //     const roleUpdate = DeviceEventEmitter.addListener("ROLE_UPDATED", () => {
    //         setOffset(0)
    //         setSearchText("")
    //     })
    //     return () => {
    //         roleUpdate?.remove()
    //     }
    // }, [])

    // ─── Delete role ──────────────────────────────────────────────────────────
    const handleDeleteRole = async () => {
        if (!selectRoleId) return;
        setIsDeleting(true);
        setIsRemovePopover(false);
        await dispatch(deletePosRole({ storeId: config?.storeId, id: selectRoleId }))
            .unwrap()
            .then((res) => {
                if (res) {
                    setSelectRoleId(null);
                    ZenToast({ message: res?.message, type: 'success' });
                }
            })
            .catch((err) => {
                ZenToast({ message: err?.error?.details || 'Failed to delete role.', type: 'error' });
            }).finally(() => {
                setIsDeleting(false);
            })
    };

    const deleteConfirmation = (value: { name: string }) => {
        if (value?.name === 'Yes') {
            handleDeleteRole();
        } else {
            setIsRemovePopover(false);
            setSelectRoleId(null);
        }
    };

    // ─── Render each role card ────────────────────────────────────────────────
    const RenderItem = ({ item }: { item: setRoleProps }) => {
        // Format the date or provide a fallback for null values
        const formattedDate = item.createdat
            ? new Date(item.createdat).toLocaleDateString()
            : 'No Date';
        if (item?.type === "EMPTY") {
            return <View style={{ flex: 1 }}></View>
        }
        return (
            <View style={styles.card}>
                <View style={styles.content}>
                    <View style={styles.headerRow}>
                        <ZenText size={fontSize.smallerFontSize} color={appColors.primary} content={item.name} bold />
                        <ZenBadge
                            badgeColor={"#E8F0FE"}
                            badgeName={`${item.activeUsers} Users`}
                            badgeTextSize={fontSize?.badgeFontSize}
                            badgeTextStyle={{ fontFamily: appFonts?.bold }}
                            badgeTextColor={"#1A73E8"}
                            badgeContainerStyle={{ paddingHorizontal: 8, paddingVertical: 4, borderRadius: 6 }}
                        />
                    </View>

                    <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center", flex: 1 }}>
                        <View style={{ flex: 0.8 }}>
                            <ZenText color={appColors.lightDark} size={fontSize?.smallHeaderFontSize} numberOfLines={2} content={item.description || "No description provided."} />
                            <ZenText color={appColors.dropdownPlaceHolder} size={fontSize?.errorFontSize} content={`Created: ${formattedDate}`} />
                        </View>
                        {/* Action Bar */}
                        <View style={styles.actions}>
                            {isRoleWrite && <TouchableOpacity style={[styles.iconButton, { backgroundColor: appColors.buttonColor }]} onPress={() => { setOpenInviteUser(true); setRoleInvite(item) }}>
                                <ZenIcon type='material-community' name="account-plus-outline" size={22} color="#4A90E2" />
                            </TouchableOpacity>}

                            <TouchableOpacity style={[styles.iconButton, { backgroundColor: appColors.buttonColor }]} onPress={() => navigation.navigate('AddRole', { id: item.id, roleName: item.name, isEdit: true })}>
                                <ZenIcon type='material-community' name={isRoleWrite ? "pencil-outline" : "eye"} size={22} color="#555" />
                            </TouchableOpacity>

                            {isRoleWrite && <TouchableOpacity style={[styles.iconButton, { backgroundColor: appColors.buttonColor }]} onPress={() => { setIsRemovePopover(true); setSelectRoleId(item.id) }}>
                                <ZenIcon type='material-community' name="trash-can-outline" size={22} color="#FF5252" />
                            </TouchableOpacity>}
                        </View>
                    </View>
                </View>
            </View>
        );
    }



    return (
        <ThemeConsumer>
            {({ theme }: { theme: Theme }) => (
                <KeyboardAvoidingView style={theme?.roleStyle?.container}>
                    <CommonHeader headerName='Roles & Permission' />
                    <ListToolbar
                        search={{
                            value: searchText ?? "",
                            onChange: (val) => setSearchText(val as string),
                            onCancel: () => setSearchText(""),
                            placeholder: 'Search by Roles',
                        }}
                        buttons={isRoleWrite ? [{
                            onPress: () => navigation.navigate('AddRole', { id: null, isEdit: false, roleName: '' }),
                            title: "Add Roles",
                            variant: "primary"
                        }] : []}
                    />
                    {/* Full-screen loader (only on first load, not refresh/pagination) */}
                    {loading && !offset && (!listRefresh || !rolesReduxData) || isDeleting ? (
                        <ZenLoader lottieType='Local' lottie={require('../../assets/infinityLoader.json')} />
                    ) : (
                        <View style={{ flex: 1 }}>
                            <FlatList
                                data={visibleRoles}
                                renderItem={RenderItem}
                                keyExtractor={(_: any, index: number) => index.toString()}
                                numColumns={width > height ? 2 : 1}
                                key={width > height ? 'two-col' : 'one-col'}
                                scrollEnabled
                                showsVerticalScrollIndicator={false}
                                contentContainerStyle={{
                                    paddingBottom: visibleRoles.length ? 10 : undefined,
                                    flexGrow: 1,
                                    gap: visibleRoles.length ? 12 : undefined,
                                    paddingHorizontal: 12,
                                    paddingTop: 8,
                                }}
                                columnWrapperStyle={width > height ? { gap: 12 } : undefined}
                                ListEmptyComponent={<ZenEmptyComponent />}
                                onRefresh={() => {
                                    setListRefresh(true);
                                    // setOffset(0);
                                    setSearchText("")
                                    fetchRoles(true);
                                }}
                                refreshing={listRefresh}
                                onEndReached={() => {
                                    if (!localIsReached && !loading) {
                                        setOffset((prev) => prev + LIMIT);
                                    }
                                }}
                                onEndReachedThreshold={0.5}
                                ListFooterComponentStyle={{ height: visibleRoles.length ? 20 : undefined }}
                                ListFooterComponent={
                                    !localIsReached && visibleRoles.length > 0 ? (
                                        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', height: 50 }}>
                                            <ZenLoader size={'small'} color={appColors?.primary} />
                                        </View>
                                    ) : <></>
                                }
                            />
                        </View>
                    )}

                    {/* FAB — mobile add button */}
                    {width < 600 && !loading && isRoleWrite && (
                        <View style={theme?.roleStyle?.plusIconView}>
                            <ZenIcon name='plus' type='entypo' size={26} color={appColors.light} onPress={() => navigation.navigate('AddRole')} />
                        </View>
                    )}

                    {/* Invite Staff Modal */}
                    <CustomModal visible={openInviteUser} transparent animationType='slide'>
                        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: appColors.modalBg100 }}>
                            <Pressable style={StyleSheet.absoluteFill} onPress={() => Keyboard.dismiss()} />
                            <InviteUser setOpenInviteUser={setOpenInviteUser} roleInvite={roleInvite!} />
                            <Toast config={ZenToastConfig} position='bottom' bottomOffset={30} />
                        </View>
                    </CustomModal>
                    {/* Delete confirm popover */}
                    <Popover isVisible={isRemovePopover} popoverStyle={{ width: width > 900 ? 600 : width > 600 ? 500 : 300, borderRadius: 10 }}>
                        <CommonDialogBox type='DELETE' replaceValue='role' confirmation={deleteConfirmation} />
                    </Popover>
                    {
                        width < height &&
                        <Pressable
                            style={theme?.staffListStyle?.fabButton}
                            onPress={() => navigation.navigate('AddRole', { id: null, isEdit: false, roleName: '' })}>
                            <ZenIcon type="material-community" name={"plus"} size={25} color={theme?.light} />
                        </Pressable>
                    }
                </KeyboardAvoidingView>
            )}
        </ThemeConsumer>
    );
};

export default RoleList;

const styles = StyleSheet.create({
    card: {
        flex: 1,
        backgroundColor: '#FFF',
        borderRadius: 12,
        marginBottom: 16,
        elevation: 3, // Shadow for Android
        shadowColor: '#000', // Shadow for iOS
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
        overflow: 'hidden'
    },
    content: { padding: 16 },
    headerRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 8
    },
    actions: {
        flexDirection: 'row',
        flex: 0.2,
        gap: 15,
        justifyContent: "flex-end"
    },
    iconButton: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 10,
        borderRightWidth: 0.5,
        borderRightColor: '#EEE',
        borderRadius: 10
    },
    actionLabel: { marginLeft: 6, fontSize: 13, fontWeight: '500', color: '#555' }
});